import React, { useState, useEffect } from "react";
import { FileJson, RefreshCw } from "lucide-react";
import { SwaggerDoc } from "../../SwaggerDoc";

// Define the base URL for the Swagger documentation
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const SWAGGER_API_URL = `${API_BASE_URL}/docs-json`;
console.log("API Base URL", API_BASE_URL);
// Define types for our tabs and specs
type TabType = "authentication" | "configuration" | "booking" | "competition";

interface SwaggerSpec {
  [key: string]: any;
}

// Define tag patterns for each tab
const TAB_TAG_PATTERNS: Record<TabType, string> = {
  authentication: "Authentication - v1",
  configuration: "PMS Configuration - v1",
  booking: "PMS Booking - v1",
  competition: "Competition - v1",
};

export function ApiDocumentation() {
  const [activeTab, setActiveTab] = useState<TabType>("authentication");
  const [originalSpec, setOriginalSpec] = useState<any>(null);
  const [specs, setSpecs] = useState<SwaggerSpec>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Function to filter the Swagger specification based on the active tab
  const filterSpecByTab = (spec: any, tab: TabType): any => {
    if (!spec || !spec.paths) {
      return spec;
    }

    // For description tab, create a special version with just the info and a single path
    // This ensures the Swagger UI shows the description properly
    // if (tab === "description") {
    //   // Create a minimal spec with just the info section and a dummy path
    //   // This ensures the Swagger UI renders properly
    //   return {
    //     ...spec,
    //     paths: {
    //       "/api-info": {
    //         get: {
    //           summary: "API Information",
    //           description: "General information about the API",
    //           tags: ["Description"],
    //           responses: {
    //             "200": {
    //               description: "API Information",
    //               content: {
    //                 "application/json": {
    //                   schema: {
    //                     type: "object",
    //                     properties: {
    //                       message: {
    //                         type: "string",
    //                         example:
    //                           "See the description above for API information",
    //                       },
    //                     },
    //                   },
    //                 },
    //               },
    //             },
    //           },
    //         },
    //       },
    //     },
    //   };
    // }

    const tagPattern = TAB_TAG_PATTERNS[tab];
    const filteredPaths: any = {};

    // Filter paths based on tags
    Object.entries(spec.paths).forEach(([path, pathItem]: [string, any]) => {
      const methods = ["get", "post", "put", "delete", "patch"];
      let shouldIncludePath = false;

      methods.forEach((method) => {
        if (pathItem[method] && pathItem[method].tags) {
          // Make the tag matching more flexible
          const matchesTag = pathItem[method].tags.some((tag: string) => {
            // For configuration tab, match any tag containing "Configuration" or "config"
            if (tab === "authentication") {
              return (
                tag.toLowerCase().includes("authentication") ||
                tag.toLowerCase().includes("auth")
              );
            } else if (tab === "configuration") {
              return (
                tag.toLowerCase().includes("configuration") ||
                tag.toLowerCase().includes("config")
              );
            }
            // For booking tab, match any tag containing "Booking" or "book"
            else if (tab === "booking") {
              return (
                tag.toLowerCase().includes("booking") ||
                tag.toLowerCase().includes("book")
              );
            }
            // For competition tab, match any tag containing "Competition"
            else if (tab === "competition") {
              return tag.toLowerCase().includes("competition");
            }
            // Default behavior: check if tag includes the pattern
            return tag.includes(tagPattern);
          });

          if (matchesTag) {
            shouldIncludePath = true;
          }
        }
      });

      if (shouldIncludePath) {
        filteredPaths[path] = pathItem;
      }
    });

    // If no paths were found for this tab, add a message endpoint
    if (Object.keys(filteredPaths).length === 0) {
      filteredPaths[`/no-${tab}-endpoints`] = {
        get: {
          summary: `No ${tab} endpoints found`,
          description: `No endpoints were found for the ${tab} tab. This may be because the API doesn't have any endpoints tagged with "${tagPattern}".`,
          tags: [TAB_TAG_PATTERNS[tab]],
          responses: {
            "200": {
              description: "Empty response",
              content: {
                "application/json": {
                  schema: {
                    type: "object",
                    properties: {
                      message: {
                        type: "string",
                        example: `No ${tab} endpoints available`,
                      },
                    },
                  },
                },
              },
            },
          },
        },
      };
      console.log(`No endpoints found for ${tab} tab, adding placeholder`);
    }

    return {
      ...spec,
      paths: filteredPaths,
    };
  };

  // Function to fetch the Swagger specification
  const fetchSwaggerSpec = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch the Swagger specification from the API
      const response = await fetch(SWAGGER_API_URL);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch Swagger specification: ${response.statusText}`
        );
      }

      const data = await response.json();
      setOriginalSpec(data);

      // Filter specs for each tab
      const filteredSpecs: SwaggerSpec = {
        // description: filterSpecByTab(data, "description"),
        authentication: filterSpecByTab(data, "authentication"),
        configuration: filterSpecByTab(data, "configuration"),
        booking: filterSpecByTab(data, "booking"),
        competition: filterSpecByTab(data, "competition"),
      };

      // Log the filtered specs for debugging
      console.log(
        "Original spec paths count:",
        Object.keys(data.paths || {}).length
      );

      // Log all available tags in the spec for debugging
      const allTags = new Set<string>();
      Object.values(data.paths || {}).forEach((pathItem: any) => {
        ["get", "post", "put", "delete", "patch"].forEach((method) => {
          if (pathItem[method] && pathItem[method].tags) {
            pathItem[method].tags.forEach((tag: string) => allTags.add(tag));
          }
        });
      });
      console.log("Available tags in the spec:", Array.from(allTags));

      // Log the filtered specs paths count
      console.log("Filtered specs paths count:", {
        // description: Object.keys(filteredSpecs.description.paths || {}).length,
        configuration: Object.keys(filteredSpecs.configuration.paths || {})
          .length,
        booking: Object.keys(filteredSpecs.booking.paths || {}).length,
        competition: Object.keys(filteredSpecs.competition.paths || {}).length,
      });

      setSpecs(filteredSpecs);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error fetching Swagger specification:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch the Swagger specification when the component mounts
  useEffect(() => {
    fetchSwaggerSpec();
  }, []);

  // Function to handle manual refresh
  const handleRefresh = () => {
    fetchSwaggerSpec();
  };

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileJson className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-bold text-gray-700">
              API Documentation
            </h2>
          </div>
          <div className="flex items-center">
            {lastUpdated && (
              <span className="text-sm text-gray-500 mr-3">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={handleRefresh}
              className="flex items-center px-3 py-1 text-sm font-medium text-indigo-600 border border-indigo-300 rounded-md hover:bg-indigo-50"
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`}
              />
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            <p className="text-sm">{error}</p>
            <p className="text-sm mt-1">
              Using cached documentation if available. Please try refreshing
              again later.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {/* <button
              onClick={() => setActiveTab("description")}
              className={`
                flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === "description"
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              Description
            </button> */}
            <button
              onClick={() => setActiveTab("authentication")}
              className={`
                flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === "authentication"
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              Authentication
            </button>
            <button
              onClick={() => setActiveTab("configuration")}
              className={`
                flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === "configuration"
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              PMS Configuration
            </button>
            <button
              onClick={() => setActiveTab("booking")}
              className={`
                flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === "booking"
                    ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              PMS Booking
            </button>
            <button
              onClick={() => setActiveTab("competition")}
              className={`
                flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === "competition"
                    ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              Competition
            </button>
          </nav>
        </div>

        {/* Swagger UI */}
        <div className="bg-white">
          {loading && !specs[activeTab] ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="h-8 w-8 text-indigo-500 animate-spin" />
              <span className="ml-2 text-gray-600">
                Loading API documentation...
              </span>
            </div>
          ) : specs[activeTab] ? (
            <SwaggerDoc spec={specs[activeTab]} />
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">
                No documentation available. Please try refreshing.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
